import { URL_BASE } from '@/config'
import { ITheme, Language } from '@/lib/interfaces/interfaces'
import '@/sass/menu/Footer.scss'
import Link from 'next/link'

interface StructuredAddress {
  streetAddress?: string
  locality?: string
  region?: string
  postalCode?: string
  country?: string
  coordinates?: {
    lat?: number
    lng?: number
  }
}

interface Props {
  theme: ITheme
  address?: string
  structuredAddress?: StructuredAddress
  phone?: string
  name: string
  bookingsActive: boolean
  slug: string
  language: Language
}

const Footer = ({ theme, address, structuredAddress, phone, name, slug, bookingsActive, language }: Props) => {
  const normalizeAddressPart = (value?: string) => value?.replace(/\s+/g, ' ').trim() || ''

  const normalizeAddressForCompare = (value: string) => {
    return normalizeAddressPart(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
  }

  const dedupeAddressParts = (parts: string[]) => {
    const seen = new Set<string>()

    return parts.filter(part => {
      const normalized = normalizeAddressForCompare(part)
      if (!normalized || seen.has(normalized)) return false
      seen.add(normalized)
      return true
    })
  }

  const removePostalCodeFromPart = (value: string) => {
    let cleaned = normalizeAddressPart(value)
      .replace(/^(?:c\.?\s*p\.?|codigo postal|código postal|postal code|code postal|postleitzahl)\s*:?\s*/i, '')
      .trim()

    cleaned = cleaned.replace(/^\d{4,6}\b[\s,-]*/i, '').trim()
    cleaned = cleaned.replace(/[\s,-]*\b\d{4,6}\b$/i, '').trim()

    return cleaned
  }

  const isSpainCountry = (value?: string) => {
    const normalized = normalizeAddressForCompare(value || '')
    return [
      'es',
      'espana',
      'espanya',
      'spain',
      'espagne',
      'spanien',
      'espainia'
    ].includes(normalized)
  }

  const formatStructuredAddress = (value?: StructuredAddress) => {
    if (!value) return ''

    const streetAddress = normalizeAddressPart(value.streetAddress)
    const locality = normalizeAddressPart(value.locality)
    const region = normalizeAddressPart(value.region)
    const country = normalizeAddressPart(value.country)
    const countryToShow = country && !isSpainCountry(country) ? country : ''

    const locationParts = dedupeAddressParts([locality, region])
    const primaryLocation = locationParts[0] || ''

    const firstLine = dedupeAddressParts([streetAddress]).join(', ')
    const secondLine = dedupeAddressParts([
      primaryLocation,
      ...locationParts.slice(1),
      countryToShow
    ]).join(', ')

    return [firstLine, secondLine].filter(Boolean).join(', ')
  }

  const formatLegacyAddress = (value?: string) => {
    const parts = (value || '')
      .split(',')
      .map(removePostalCodeFromPart)
      .filter(Boolean)

    return dedupeAddressParts(parts)
      .filter(part => !isSpainCountry(part))
      .join(', ')
  }

  const formattedAddress = formatStructuredAddress(structuredAddress) || formatLegacyAddress(address)

  const getMapsUrl = () => {
    const streetAddress = normalizeAddressPart(structuredAddress?.streetAddress)
    const locality = normalizeAddressPart(structuredAddress?.locality)
    const region = normalizeAddressPart(structuredAddress?.region)
    const country = normalizeAddressPart(structuredAddress?.country)
    const structuredQuery = dedupeAddressParts([streetAddress, locality, region, country]).join(', ')
    const fallbackQuery = formattedAddress.replace(/\n/g, ', ')
    const query = structuredQuery || fallbackQuery

    if (!query) return ''

    // Including the bar name improves the chance of opening the business profile instead of a raw address pin.
    const businessQuery = dedupeAddressParts([normalizeAddressPart(name), query]).join(', ')

    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(businessQuery || query)}`
  }

  const mapsUrl = getMapsUrl()

  const getMenuText = () => {
    switch (language) {
      case 'en':
        return `${name}'s digital menu`
      case 'fr':
        return `Menu numérique de ${name}`
      case 'de':
        return `Digitale Speisekarte von ${name}`
      case 'ca':
        return `Menú digital de ${name}`
      case 'eu':
        return `${name} menu digitala`
      case 'es':
      default:
        return `Menú digital de ${name}`
    }
  }

  const getBookingText = () => {
    switch (language) {
      case 'en':
        return 'Book your table here'
      case 'fr':
        return 'Réservez votre table ici'
      case 'de':
        return 'Reservieren Sie hier Ihren Tisch'
      case 'ca':
        return 'Reserva la teva taula aquí'
      case 'eu':
        return 'Erreserbatu zure mahaia hemen'
      case 'es':
      default:
        return 'Reserva tu mesa aquí'
    }
  }

  return (
    <footer style={{ backgroundColor: theme.footer.backgroundColor }} className='footer'>
      <div className='contac-container' style={{ color: theme.footer.color }}>
        <p>{getMenuText()}</p>
        {bookingsActive &&
          <p>
            <Link
              style={{ color: theme.footer.color }}
              href={`${URL_BASE}/booking/${slug}`}
              className='booking-btn'
            >
              <span>{getBookingText()}</span>
            </Link>
          </p>}
        {formattedAddress && (
          <p className='footer-address'>
            {mapsUrl
              ? (
                <a
                  href={mapsUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='footer-address-link'
                >
                  <svg
                    className='footer-address-icon'
                    xmlns='http://www.w3.org/2000/svg'
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='1.8'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    aria-hidden='true'
                  >
                    <path d='M12 22s7-4.6 7-11a7 7 0 1 0-14 0c0 6.4 7 11 7 11Z' />
                    <circle cx='12' cy='11' r='2.8' />
                  </svg>
                  <span className='footer-address-text'>{formattedAddress}</span>
                </a>
                )
              : formattedAddress}
          </p>
        )}
        {phone && <p><a style={{ backgroundColor: theme.footer.backgroundColor }} href={`tel:${phone}`}>{phone}</a></p>}
      </div>
      <p className='barmanager' style={{ color: theme.footer.color }}>
        <Link prefetch={false} style={{ color: theme.footer.color }} href='/'>
          Barmanager.app · {language === 'en' ? 'Reservations and digital menu' : language === 'fr' ? 'Réservations et menu numérique' : language === 'de' ? 'Reservierungen und digitale Speisekarte' : language === 'ca' ? 'Reserves i menú digital' : language === 'eu' ? 'Erreserbak eta menu digitala' : 'Reservas y menú digital'}
        </Link>
      </p>
    </footer>
  )
}

export default Footer
